import { formatRuPhone, normalizeRuDigits, onlyDigits } from '@persona/shared';
import { forwardRef, useLayoutEffect, useRef, type InputHTMLAttributes } from 'react';
import { inputClasses } from './Field';

export interface PhoneInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  /** Только цифры номера, например `79991234567`. */
  value: string;
  onChange: (digits: string) => void;
  invalid?: boolean;
}

/**
 * Поле ввода телефона с маской `+7 (999) 123-45-67`.
 *
 * Наружу отдаются только цифры: форматирование — дело представления,
 * а проверка и нормализация выполняются общей схемой валидации.
 *
 * Основная сложность — курсор. Наивная реализация после каждого символа
 * отправляет курсор в конец строки, из-за чего невозможно исправить
 * ошибку в середине номера.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onChange, invalid, className, ...props },
  forwardedRef,
) {
  const innerRef = useRef<HTMLInputElement | null>(null);
  /** Куда поставить курсор после перерисовки; null — не трогать. */
  const caretRef = useRef<number | null>(null);

  const formatted = formatRuPhone(value);

  useLayoutEffect(() => {
    const input = innerRef.current;
    if (input && caretRef.current !== null) {
      input.setSelectionRange(caretRef.current, caretRef.current);
      caretRef.current = null;
    }
  });

  function setRefs(node: HTMLInputElement | null): void {
    innerRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }

  return (
    <input
      ref={setRefs}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      placeholder="+7 (___) ___-__-__"
      aria-invalid={invalid || undefined}
      className={inputClasses(invalid, className)}
      value={formatted}
      onKeyDown={(event) => {
        // Backspace перед символом маски: сдвигаем курсор к ближайшей цифре,
        // иначе нажатие визуально ничего не делает и поле кажется зависшим.
        if (event.key === 'Backspace') {
          const input = event.currentTarget;
          const caret = input.selectionStart ?? 0;
          if (caret > 0 && caret === input.selectionEnd && !/\d/.test(input.value[caret - 1] ?? '')) {
            event.preventDefault();
            let position = caret - 1;
            while (position > 0 && !/\d/.test(input.value[position - 1] ?? '')) position -= 1;
            if (position === 0) return;

            const digitsBefore = countDigits(input.value.slice(0, position - 1));
            const digits = onlyDigits(input.value);
            const next = normalizeRuDigits(digits.slice(0, digitsBefore) + digits.slice(digitsBefore + 1));
            caretRef.current = caretAfterDigits(formatRuPhone(next), digitsBefore);
            onChange(next);
          }
        }
      }}
      onChange={(event) => {
        const input = event.currentTarget;
        const caret = input.selectionStart ?? input.value.length;

        const rawDigits = onlyDigits(input.value);
        const digits = normalizeRuDigits(rawDigits).slice(0, 11);

        // Когда пользователь начинает с «9», маска сама подставляет код страны.
        // Без этой поправки курсор встаёт перед только что введённой цифрой,
        // и следующие символы попадают в начало номера.
        const prependedDigits = Math.max(0, digits.length - rawDigits.length);

        // Количество цифр левее курсора — единственная величина,
        // которая переживает переформатирование строки.
        const digitsBeforeCaret = countDigits(input.value.slice(0, caret)) + prependedDigits;

        caretRef.current = caretAfterDigits(formatRuPhone(digits), digitsBeforeCaret);
        onChange(digits);
      }}
      {...props}
    />
  );
});

function countDigits(value: string): number {
  return (value.match(/\d/g) ?? []).length;
}

/** Позиция сразу за N-й цифрой отформатированной строки. */
function caretAfterDigits(formatted: string, digitCount: number): number {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < formatted.length; index += 1) {
    if (/\d/.test(formatted[index] ?? '')) {
      seen += 1;
      if (seen === digitCount) return index + 1;
    }
  }
  return formatted.length;
}
